import { Check } from "lucide-react";
import Eyebrow from "@/components/zeblan/Eyebrow";
import Reveal from "@/components/zeblan/Reveal";

export default function Pricing({ onOpenLead }) {
  return (
    <section id="pricing" className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="flex justify-center">
            <Eyebrow>Pricing</Eyebrow>
          </div>
          <h2 className="mt-6 text-navy font-extrabold tracking-tightish" style={{ fontSize: "clamp(30px,4vw,46px)" }}>
            It starts with a{" "}
            <span className="font-display italic font-medium text-blue">free ROI assessment</span>
          </h2>
          <p className="mt-5 text-slate text-[17px] leading-relaxed">
            We give you a rough estimate of the hours and money an AI agent could save you, before you commit to
            anything.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Light card */}
          <Reveal>
            <article className="h-full bg-white border border-mist rounded-2xl p-9 flex flex-col">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue">Off-the-shelf</div>
              <h3 className="mt-4 text-navy text-[26px] font-extrabold tracking-tightish">Fixed fee</h3>
              <p className="mt-3 text-slate text-[15px] leading-relaxed">
                If your solution is relatively standard, we quote a single fixed fee based on complexity. Simple and
                predictable.
              </p>
              <ul className="mt-7 space-y-3 text-slate text-[15px]">
                {["One transparent fixed fee", "Scoped to complexity", "Fastest path to value"].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-blue mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onOpenLead("roi-assessment")}
                className="mt-8 inline-flex items-center justify-center border border-mist hover:border-blue hover:text-blue text-navy font-semibold px-6 py-3 rounded-md transition-colors"
                data-testid="pricing-fixed-cta"
              >
                Schedule an assessment
              </button>
            </article>
          </Reveal>

          {/* Dark card */}
          <Reveal delay={120}>
            <article
              className="h-full rounded-2xl p-9 text-white flex flex-col relative overflow-hidden border border-white/5"
              style={{ background: "linear-gradient(140deg, #0A1328 0%, #11214b 100%)" }}
            >
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal">Custom build</div>
              <h3 className="mt-4 text-[26px] font-extrabold tracking-tightish">Staged & transparent</h3>
              <p className="mt-3 text-mist/85 text-[15px] leading-relaxed">
                If your solution isn't off-the-shelf, we break it into clear, separately-quoted stages. No surprises.
              </p>
              <ul className="mt-7 space-y-3 text-mist/90 text-[15px]">
                {[
                  "Fixed fee for a technical blueprint & feasibility study",
                  "Quote for the MVP",
                  "Quote for the full rollout",
                  "Monthly aftercare package: hosting, dedicated account manager, free minor tweaks",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-teal mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onOpenLead("drop-a-line")}
                className="mt-8 inline-flex items-center justify-center bg-blue hover:bg-blue-deep text-white font-semibold px-6 py-3 rounded-md transition-colors"
                data-testid="pricing-custom-cta"
              >
                Drop us a line
              </button>
            </article>
          </Reveal>
        </div>

        <Reveal className="mt-16 max-w-5xl mx-auto">
          <div className="text-center text-[11px] font-bold uppercase tracking-[0.22em] text-steel">
            Transparent hourly rates
          </div>
          <div className="mt-6 grid md:grid-cols-3 gap-4 border-t border-b border-mist py-8">
            {[
              { rate: "€80/hr", role: "Junior developers" },
              { rate: "€120/hr", role: "Senior developers" },
              { rate: "€200/hr", role: "Lead engineers" },
            ].map((r) => (
              <div key={r.role} className="text-center">
                <div className="text-navy font-extrabold tracking-tightish" style={{ fontSize: "32px" }}>
                  {r.rate}
                </div>
                <div className="text-steel text-[14px] mt-1">{r.role}</div>
              </div>
            ))}
          </div>
          <p className="mt-5 text-center text-steel text-[13px] max-w-xl mx-auto">
            Example rates shown for transparency. Your ROI assessment will map these to your specific project.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
